import React, { FC, useEffect, useRef, useState } from "react";
import {
    FlatList,
    Pressable,
    StyleSheet,
    TouchableOpacity,
    View,
    useWindowDimensions,
} from "react-native";
import Popover from "react-native-popover-view/dist/Popover";
import { useIsFocused } from "@react-navigation/native";
import moment from "moment";

import { config } from "../../shared/environment";
import { getScreenPermission, useAppDispatch, useAppSelector } from "../../redux/hooks";
import CommonHeader from "../../shared/commonComponents/commonHeader";
import { appColors, appFonts, fontSize } from "../../themes/appTheme";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
import {
    CustomerGroupDataProps,
    FilterPayloadProps,
    FilterValueProps,
} from "./customerGroupModule";
import {
    getAllPosCustomerGroupRedux,
    onRemoveCustomerGroupRedux,
} from "../../redux/slices/customerGroupSlice";
import {
    ZenCheckBox,
    ZenEmptyComponent,
    ZenFilterModal,
    ZenIcon,
    ZenLoader,
    ZenText,
    ZenToast,
} from "zen-core-elements";
import { useAppNavigation } from "../../types/@react-navigation/navigation";
import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";
import { useKeyborad } from "../../shared/hooks/useKeyborad";
import { ListToolbar } from "../../shared/commonComponents/listToolbar";
import { useTheme } from "@rneui/themed";
import { shallowEqual } from "react-redux";

// Module-level offset (mirrors orderReport pattern — avoids stale closure issues)
const LIMIT = 10;

const CustomerGroupList: FC = () => {

    const dispatch = useAppDispatch();
    const { width, height } = useWindowDimensions();
    const navigation = useAppNavigation();
    const isFocused = useIsFocused();
    const { isWrite: isCustomerGroupWrite } = useAppSelector(getScreenPermission("CustomerGroupStack"), shallowEqual)

    // ─── Redux state ────────────────────────────────────────────────────────────
    const { loading, value, isReached, filter } = useAppSelector(
        (state) => state.customerGroup.getAllPosCustomerGroup
    );

    // ─── Local UI state ──────────────────────────────────────────────────────────
    const [listRefresh, setListRefresh] = useState(false);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [filterValue, setFilterValue] = useState<FilterPayloadProps>(undefined);
    const [isVisibleFilter, setIsVisibleFilter] = useState(false);
    const [filterCount, setFilterCount] = useState(0);
    const [deletePopover, setDeletePopover] = useState<{ isVisible: boolean; id: number | null }>({ isVisible: false, id: null });
    const [deleteList, setDeleteList] = useState<number[]>([]);
    const [offset, setOffset] = useState(0);
    const onMomentumScrollBegin = useRef(false);
    const { theme } = useTheme()
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: "1",
            filterName: "No of customers",
            filterType: "inputRange",
            initialSelected: { from: "", to: "" },
        },
        {
            id: "2",
            filterName: "Custom date",
            filterType: "dateRange",
            filterValue: { minDate: "", maxDate: "" },
            initialSelected: { startDate: "", endDate: "" },
        },
    ]);

    // ─── Build filter string ────────────────────────────────────────────────────
    const buildFilter = (fv: FilterPayloadProps, search: string) => {
        const payload: FilterValueProps = {};
        if (fv?.customersCount) {
            payload.customersCount = {
                from: fv.customersCount.from,
                to: fv.customersCount.to,
            };
        }
        if (fv?.customDateRange) {
            payload.customDateRange = {
                begin: fv.customDateRange.startDate,
                end: fv.customDateRange.endDate,
            };
        }
        return {
            globalsearch: search.trim(),
            filterData: Object.keys(payload).length ? JSON.stringify(payload) : null,
        };
    };

    // ─── API call ────────────────────────────────────────────────────────────────
    const fetchCustomerGroups = async (currentOffset?: number, forceRefresh = false) => {
        const ofs = currentOffset !== undefined ? currentOffset : 0;
        const { globalsearch, filterData: fd } = buildFilter(filterValue, searchText || "");
        const currentFilter = JSON.stringify({ globalsearch, filterData: fd });

        // Cache check — skip if same filter & first page & we already have data
        if (!forceRefresh && ofs === 0 && filter === currentFilter && value) return;

        dispatch(
            getAllPosCustomerGroupRedux({
                storeId: config.storeId,
                data: { offset: ofs, limit: LIMIT, globalsearch, filterData: fd },
            })
        )
            .unwrap()
            .catch(() => {
                ZenToast({ type: "error", message: "Error while getting Customer group details" });
            })
            .finally(() => {
                setListRefresh(false);
            });
    };

    // ─── Effects ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isFocused) {
            setOffset(0)
            fetchCustomerGroups();
        }
    }, [isFocused]);

    //useEffect triggered  when search data changes
    useEffect(() => {
        if (searchText) {
            setOffset(0)
            const debounceSearchText = setTimeout(() => {
                fetchCustomerGroups(0, true);
            }, 1000);
            return () => { clearTimeout(debounceSearchText) }
        }
        else if (searchText == '') {
            fetchCustomerGroups(0, true);
        }
    }, [searchText])

    useEffect(() => {
        if (filterValue === undefined) return;
        setOffset(0)
        fetchCustomerGroups(0, true);
    }, [filterValue]);


    // ─── Handlers ────────────────────────────────────────────────────────────────
    const deleteConfirmation = (val: { name: string }) => {
        if (val?.name === "Yes") {
            setDeletePopover({ isVisible: false, id: null });
            if (deletePopover?.id) {
                onRemoveCustomerGroup(deletePopover.id);
            } else {
                onBulkRemoveCustomerGroups();
            }
        } else {
            setDeletePopover({ isVisible: false, id: null });
        }
    };

    const onRemoveCustomerGroup = async (id: number) => {
        dispatch(onRemoveCustomerGroupRedux({ storeId: config.storeId, id }))
            .unwrap()
            .then((res) => {
                if (res?.deleteCustomerGroupResponse) {
                    setDeleteList([]);
                    setOffset(0);
                    fetchCustomerGroups(0, true);
                }
            })
            .catch((err) => console.log("Error in delete customer group", err));
    };

    const onBulkRemoveCustomerGroups = () => {
        if (!deleteList.length) return;
        // Fire delete for each selected id sequentially (no bulk API present)
        const queue = [...deleteList];
        setDeleteList([]);
        queue.forEach((id) => onRemoveCustomerGroup(id));
    };

    const selectDeselectItem = (id: number) => {
        setDeleteList((prev) => {
            const filtered = prev.filter((v) => v !== id);
            return filtered.length === prev.length ? [...prev, id] : filtered;
        });
    };

    // ─── Derived data ─────────────────────────────────────────────────────────────
    const rows: CustomerGroupDataProps[] = value?.customergroup?.rows ?? [];

    // ─── List item render ─────────────────────────────────────────────────────────
    const RenderItem = ({ item }: { item: CustomerGroupDataProps }) => {
        const isSelected = deleteList.includes(item.id);
        return (
            <Pressable
                onPress={() => {
                    if (deleteList.length && isCustomerGroupWrite) {
                        selectDeselectItem(item.id);
                    }
                }}
                onLongPress={() => {
                    if (!deleteList.length && isCustomerGroupWrite) {
                        setDeleteList([item.id]);
                    }
                }}
                style={({ pressed }) => [
                    styles.card,
                    isSelected && styles.cardSelected,
                    (pressed && width < height && isCustomerGroupWrite) && styles.cardPressed,
                ]}
            >
                {/* Left — checkbox (tablet) + info */}
                <View style={styles.cardLeft}>
                    {width > height && isCustomerGroupWrite && (
                        <ZenCheckBox
                            onPress={() => selectDeselectItem(item.id)}
                            checked={isSelected}
                            checkedColor={appColors.primary}
                            unCheckedColor={appColors.lightGrey}
                        />
                    )}

                    <View style={{ flex: 1 }}>
                        {/* Group name + badge */}
                        <View style={{ flexDirection: "row", flexWrap: "wrap", paddingBottom: 4, alignItems: "center", gap: 8 }}>
                            <ZenText
                                style={{
                                    fontFamily: appFonts.bold,
                                    fontSize: fontSize.mediumFontSize,
                                    color: appColors.lightDark,
                                }}
                                content={
                                    item.groupName?.length > 25
                                        ? item.groupName.slice(0, 25) + "…"
                                        : item.groupName
                                }
                            />
                            <View style={styles.badge}>
                                <ZenText
                                    style={{
                                        fontFamily: appFonts.bold,
                                        fontSize: fontSize.badgeFontSize,
                                        color: appColors.lightDark,
                                    }}
                                    content={`Customers: ${item.customersCount}`}
                                />
                            </View>
                            {/* <ZenBadge 
                                badgeName={`Customers: ${item.customersCount}`}
                                badgeContainerStyle={{ backgroundColor: appColors.primary, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 5 }}
                                badgeTextStyle={{ color: appColors.light, fontFamily: appFonts.medium, fontSize: fontSize.badgeFontSize }}
                            /> */}
                        </View>

                        {/* Created date */}
                        <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                            <ZenText
                                content="Created on "
                                color={appColors.labelColor}
                                style={{ fontFamily: appFonts.medium, fontSize: fontSize.smallHeaderFontSize }}
                            />
                            <ZenText
                                content={moment(item.createdAt).format("YYYY-MM-DD")}
                                color={appColors.lightDark}
                                bold
                                style={{ fontSize: fontSize.smallHeaderFontSize }}
                            />
                        </View>
                    </View>
                </View>

                {/* Right — action icons */}
                <View style={styles.actions}>
                    <TouchableOpacity
                        style={styles.actionBtn}
                        onPress={() =>
                            navigation.navigate("AddEditCustomerGroups", { isView: true, id: item.id })
                        }
                    >
                        <ZenIcon name="eye-outline" type="ionicon" size={20} color={appColors.iconColor} />
                    </TouchableOpacity>

                    {isCustomerGroupWrite && <TouchableOpacity
                        style={styles.actionBtn}
                        onPress={() =>
                            navigation.navigate("AddEditCustomerGroups", { isEdit: true, id: item.id, customerCount: item.customersCount })
                        }
                    >
                        <ZenIcon name="pencil" type="octicon" size={18} color={appColors.iconColor} />
                    </TouchableOpacity>}

                    {isCustomerGroupWrite && <TouchableOpacity
                        style={styles.actionBtn}
                        onPress={() => setDeletePopover({ isVisible: true, id: item.id })}
                    >
                        <ZenIcon name="delete-outline" type="material-community" size={20} color={appColors.red} />
                    </TouchableOpacity>}
                </View>
            </Pressable>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <View style={{ flex: 1, backgroundColor: "whitesmoke" }}>
            <CommonHeader headerName="Customer Groups" />
            <ListToolbar
                search={{
                    value: searchText ?? "",
                    onChange: (val) => setSearchText(val as string),
                    onCancel: () => setSearchText(null),
                    placeholder: 'Search Customer Groups...',
                }}
                filter={{
                    count: filterCount ?? 0,
                    onPress: () => setIsVisibleFilter(true),
                }}
                buttons={isCustomerGroupWrite ? [{
                    onPress: () => navigation.navigate("AddEditCustomerGroups", { isAdd: true }),
                    title: "Add Customer Group",
                    variant: "primary"
                }] : []}
            />
            <View style={{ paddingHorizontal: 15 }}>
                {deleteList?.length ? <View style={styles.selectionHeader}>
                    <View style={styles.selectionLeft}>
                        <ZenCheckBox
                            title={deleteList.length === rows.length ? "Deselect All" : "Select All"}
                            containerStyle={{ marginLeft: 0, paddingLeft: 0 }}
                            checkedColor={appColors.primary}
                            checked={deleteList.length === rows.length}
                            onPress={() => {
                                if (deleteList.length === rows.length) {
                                    setDeleteList([]);
                                } else {
                                    setDeleteList(rows.map((r) => r.id));
                                }
                            }}
                            size={20}
                        />
                    </View>
                    {width > height && (
                        <TouchableOpacity
                            style={styles.bulkDeleteBtn}
                            onPress={() => setDeletePopover({ isVisible: true, id: null })}
                        >
                            <ZenIcon type="material-community" name="delete" size={22} color={appColors.red} />
                            <ZenText content="Delete" style={{ color: appColors.red, fontFamily: appFonts.medium }} />
                        </TouchableOpacity>
                    )}
                </View> : null}
            </View>
            {/* Full-screen loader on first page */}
            {loading && !offset && !listRefresh && !value ? (
                <ZenLoader
                    lottieType="Local"
                    lottie={require("../../assets/infinityLoader.json")}
                />
            ) : (
                <FlatList
                    data={rows}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={RenderItem}
                    ListEmptyComponent={ZenEmptyComponent}
                    contentContainerStyle={{
                        paddingHorizontal: 12,
                        paddingBottom: rows.length > 0 ? 20 : undefined,
                        flexGrow: rows.length > 0 ? undefined : 1,
                        gap: 15,
                    }}
                    showsVerticalScrollIndicator={false}
                    onEndReachedThreshold={0.5}
                    onMomentumScrollBegin={() => {
                        onMomentumScrollBegin.current = true;
                    }}
                    onEndReached={() => {
                        if (!onMomentumScrollBegin.current) return;
                        onMomentumScrollBegin.current = false;
                        if (!isReached && !loading) {
                            const newOffset = offset + LIMIT;
                            setOffset(newOffset)
                            fetchCustomerGroups(newOffset);
                        }
                    }}
                    onRefresh={() => {
                        setListRefresh(true);
                        setSearchText("");
                        setFilterValue(null);
                        setDeleteList([]);
                        setOffset(0);
                        fetchCustomerGroups(0, true);
                    }}
                    refreshing={listRefresh}
                    ListFooterComponent={
                        !isReached && rows.length > 0 ? (
                            <View style={{ alignItems: "center", height: 50, justifyContent: "center" }}>
                                <ZenLoader size="small" color={appColors.primary} />
                            </View>
                        ) : null
                    }
                    ListFooterComponentStyle={{ height: rows.length > 0 ? 20 : undefined }}
                />
            )}

            {/* Filter modal */}
            <ZenFilterModal
                visible={isVisibleFilter}
                setVisible={setIsVisibleFilter}
                title="Customer Group Filter"
                filterItems={filterData}
                filterCount={filterCount}
                setFilterCount={setFilterCount}
                setFilterItems={setFilterData}
                onConfirm={(val) => {
                    let data: FilterPayloadProps = {};
                    if (val?.values?.["No of customers"]) {
                        data.customersCount = val.values["No of customers"];
                    }
                    if (val?.values?.["Custom date"]) {
                        data.customDateRange = val.values["Custom date"];
                    }
                    setFilterValue(data);
                }}
            />
            {
                width < height && isCustomerGroupWrite &&
                <Pressable
                    style={theme?.staffListStyle?.fabButton}
                    onPress={() => navigation.navigate("AddEditCustomerGroups", { isAdd: true })}>
                    <ZenIcon type="material-community" name={"plus"} size={25} color={theme?.light} />
                </Pressable>
            }
            {/* Delete confirmation */}
            <Popover
                isVisible={deletePopover.isVisible}
                popoverStyle={{ width: width > height ? 500 : 350, borderRadius: 10 }}
            >
                <CommonDialogBox
                    type="DELETE"
                    replaceValue=""
                    confirmation={deleteConfirmation}
                />
            </Popover>
        </View>
    );
};

export default CustomerGroupList;

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 10,
        gap: 8,
    },
    filterBtn: {
        borderWidth: 0,
        marginHorizontal: 5,
        padding: 4,
    },
    card: {
        backgroundColor: appColors.light,
        borderRadius: 15,
        padding: 12,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        elevation: 1,
        shadowColor: "#000",
        shadowOpacity: 0.04,
        shadowRadius: 4,
    },
    cardSelected: {
        borderWidth: 1.5,
        borderColor: appColors.primary,
    },
    cardPressed: {
        opacity: 0.75,
    },
    cardLeft: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    badge: {
        paddingVertical: 3,
        paddingHorizontal: 8,
        borderRadius: 9,
        backgroundColor: "#ffedbd",
    },
    actions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    actionBtn: {
        padding: 8,
        backgroundColor: appColors.lightBackgroundColor,
        borderRadius: 8,
    },
    selectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 8,
        paddingHorizontal: 4,
    },
    selectionLeft: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
    },
    bulkDeleteBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: appColors.lightBackgroundColor,
        borderWidth: 1,
        borderColor: appColors.red,
    },
});